import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Modal } from '@/common';
import { UploadButton } from '@/components/common';
import { Button, SelectableCard } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE, UPLOAD_PICKER_TYPES } from '@/constants';
import { AssetScope, AssetType, COLORS } from '@/enums';
import { isVideoAsset } from '@/helpers';
import { useFileUpload } from '@/hooks';
import { PublishIcons } from '@/resources';
import { SidebarAssets } from '@/resources/icons/sidebar';
import {
  useGetEventAssets,
  useGetEventByIdQuery,
  useGetOrganizationAssets,
  useGetPublicAssets
} from '@/services';
import { Asset } from '@/types';

type AddPhotosModalProps = {
  onClose: () => void;
  onSubmit: (asset: Asset) => void;
  isOpen: boolean;
  modalForBackgroundLibrary?: boolean;
};

export const AddPhotosModal = ({
  onClose,
  onSubmit,
  isOpen,
  modalForBackgroundLibrary = false
}: AddPhotosModalProps) => {
  const { eventId, organizationId } = useParams();
  const { data: eventData } = useGetEventByIdQuery(Number(eventId));
  const { t } = useTranslation('common');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const { data: allAssets } = useGetEventAssets(
    eventId ?? '',
    [],
    PAGE_SIZE,
    undefined,
    [AssetType.IMAGE.toString()],
    undefined,
    true
  );

  const { data: organizationAssetsData } = useGetOrganizationAssets(
    PAGE_SIZE,
    organizationId !== undefined,
    Number(organizationId),
    '',
    [AssetType.IMAGE.toString()],
    'CreatedAt',
    false
  );

  const { data: publicAssetsData } = useGetPublicAssets(
    [''],
    PAGE_SIZE,
    isOpen && modalForBackgroundLibrary,
    '',
    [AssetType.COVER_PHOTO.toString()]
  );

  const uploadedAssetsData: Asset[] = useMemo(() => {
    if (organizationId) {
      return organizationAssetsData?.pages.flatMap((page) => page.data.items) || [];
    } else if (eventId) {
      return allAssets?.pages.flatMap((page) => page.data.items) || [];
    }

    return [];
  }, [allAssets, organizationAssetsData, eventId, organizationId]);

  const { uploadFiles, isUploading } = useFileUpload({
    eventId,
    organizationId: organizationId ?? eventData?.data?.organization?.id ?? null,
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    isPublic: false,
    assetScope: eventId ? AssetScope.PRIVATE : AssetScope.ORGANIZATION,
    onCreatedAsset: (asset) => onSubmit(asset)
  });

  const publicAssets: Asset[] = useMemo(
    () =>
      (publicAssetsData?.pages.flatMap((page) => page.data.items) || []).filter(
        (asset: Asset) => !isVideoAsset(asset)
      ),
    [publicAssetsData]
  );

  const assetData = useMemo(
    () => (modalForBackgroundLibrary ? publicAssets : uploadedAssetsData),
    [modalForBackgroundLibrary, publicAssets, uploadedAssetsData]
  );

  const handleOnSubmit = () => {
    if (selectedAsset) {
      onSubmit(selectedAsset);
    }
  };

  return createPortal(
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      label={modalForBackgroundLibrary ? t('publicAssets') : t('addNewPhoto')}
      description={modalForBackgroundLibrary ? t('pleaseChoosePhotoToUseAsBackground') : undefined}
      modalClasses='max-h-[60vh] md:w-[50vw] w-full flex-1'
      containerClasses='flex flex-col gap-5 px-6 py-5'
      height='h-full'>
      {!modalForBackgroundLibrary && (
        <>
          <UploadButton
            showMaxFileSize={false}
            multiple={false}
            text='uploadNewPhoto'
            pickerTypes={UPLOAD_PICKER_TYPES.IMAGE}
            variant={ButtonVariant.QUINARY}
            isUploading={isUploading}
            disabled={isUploading}
            className='w-full'
            icon={
              organizationId ? (
                <SidebarAssets className='mr-1 size-5 text-primary-blue' />
              ) : (
                <PublishIcons fill={COLORS.PRIMARY_BLUE} className='mr-1 size-4' />
              )
            }
            onFilesSelected={uploadFiles}
          />
          {assetData.length > 0 && (
            <div className='w-full text-center'>{t('orSelectFromOneOfTheExistingItem')}</div>
          )}
        </>
      )}
      <div className='custom-scrollbar grid grid-cols-2 gap-4 overflow-y-auto px-1 md:grid-cols-5'>
        {assetData.map((item) => (
          <SelectableCard
            key={item.id}
            imageUrl={item.thumbnailUrl}
            isSelected={selectedAsset?.id === item.id}
            label={item.assetName}
            onSelect={() =>
              setSelectedAsset((prevSelected) => (prevSelected?.id === item.id ? null : item))
            }
          />
        ))}
      </div>
      <div className='flex justify-end gap-4'>
        <Button handleOnClick={onClose} variant={ButtonVariant.QUATERNARY}>
          {t('cancel')}
        </Button>
        <Button
          handleOnClick={handleOnSubmit}
          disabled={selectedAsset === null}
          variant={ButtonVariant.PRIMARY}>
          {t('addPhoto')}
        </Button>
      </div>
    </Modal>,
    document.body
  );
};
